import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, Box, IconButton, Typography,
  Stack, CircularProgress, Accordion, AccordionSummary,
  AccordionDetails, Chip, Divider, Grid
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  DateRange as DateIcon,
  FiberManualRecord as TimelineDot
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getLeavePolicyVersionsByLeaveGroupId } from '../../../../actions/leave';
import LeavePolicyDetail from './LeavePolicyDetail';
import { getDayjsFormatWithTranslate } from '../../../../utils';
import { getName } from '../../../../utils/userData';

const LeavePolicyVersionModal = ({ open, onClose, idLeaveGroup }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && idLeaveGroup) {
      fetchLeavePolicyVersions();
    }
    if (!open) {
      setData(null);
    }
  }, [open, idLeaveGroup]);

  const fetchLeavePolicyVersions = async () => {
    setLoading(true);
    try {
      const response = await dispatch(getLeavePolicyVersionsByLeaveGroupId(idLeaveGroup));
      if (response && response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDateLabel = (date, isEnd = false) => {
    if (!date) return isEnd ? t("Current") : "-";
    return getDayjsFormatWithTranslate(date, "DD MMMM YYYY", i18n.language);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth 
      PaperProps={{ sx: { borderRadius: '20px', bgcolor: '#f8fafc' } }}
    >
      <DialogTitle sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight="800">
            {data && data.leaveGroupName? getName({name: data.leaveGroupName, name_EN: data.leaveGroupName_EN}): ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box textAlign="center" py={10}><CircularProgress /></Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Timeline Line */}
            <Box sx={{
              position: 'absolute', left: '19px', top: '20px', bottom: '20px',
              width: '2px', bgcolor: '#cbd5e1', zIndex: 0
            }} />

            <Stack spacing={2}>
              {data && data.versions && data.versions.map((v) => {
                const isActive = v.status === 'active';
                const color = v.status === 'active' ? '#22c55e' : (v.status === 'upcoming' ? '#3b82f6' : '#94a3b8');
                
                return (
                  <Accordion 
                    key={v.idLeaveEffective}
                    defaultExpanded={isActive}
                    sx={{
                      borderRadius: '16px !important',
                      border: `1px solid ${isActive ? color : '#e2e8f0'}`,
                      boxShadow: isActive ? '0 10px 15px -3px rgba(0,0,0,0.1)' : 'none',
                      '&:before': { display: 'none' },
                      zIndex: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
                        <TimelineDot sx={{ color: color, fontSize: '14px', zIndex: 2, bgcolor: 'white', borderRadius: '50%' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <DateIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="subtitle1" fontWeight="800">
                              {getDateLabel(v.startDate)} — {getDateLabel(v.endDate, true)}
                            </Typography>
                            <Chip 
                              label={t(`LeavePolicy.Status.${v.status}`)} 
                              size="small" 
                              sx={{ bgcolor: `${color}15`, color: color, fontWeight: 'bold', borderRadius: '6px' }} 
                            />
                          </Stack>
                        </Box>
                      </Stack>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ p: 0 }}>
                      <Divider />
                      <Box sx={{ p: 3, bgcolor: '#f8fafc' }}> {/* พื้นหลังอ่อนๆ ให้ตัว Paper ของลูกเด่นขึ้น */}
                        {v.leaveTypeList && v.leaveTypeList.map((item, idx) => (
                          <LeavePolicyDetail 
                            key={item.idLeaveType} 
                            item={item} 
                            index={idx} 
                            variant="admin" 
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeavePolicyVersionModal;